import { ReactNode, useRef } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";

interface ConfirmAlertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  isDestructive?: boolean;
}

export function ConfirmAlertDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  isLoading,
  isDestructive,
}: ConfirmAlertDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent
          bg="charcoal.charcoal_95"
          color="dune.dune"
          border="1px solid"
          borderColor="transparent.white.10"
          backdropFilter="blur(18px)"
          borderRadius="16px"
          mx="16px"
        >
          <AlertDialogHeader
            fontSize="18px"
            fontWeight="700"
            fontFamily="Bricolage Grotesque"
          >
            {title}
          </AlertDialogHeader>
          <AlertDialogBody color="dune.100" fontSize="14px">
            {body}
          </AlertDialogBody>
          <AlertDialogFooter gap="8px">
            <Button
              ref={cancelRef}
              onClick={onClose}
              variant="ghost"
              color="dune.dune"
              _hover={{ bg: "transparent.white.10" }}
              isDisabled={isLoading}
            >
              {cancelLabel}
            </Button>
            <Button
              onClick={onConfirm}
              isLoading={isLoading}
              // Destructive and primary actions share the blaze treatment —
              // GC's design system uses blaze as the canonical "act" color.
              // The destructive variant only differs in hover/active depth
              // (deeper blaze.600) so the press feedback feels weightier
              // for irreversible actions.
              bg="blaze.blaze"
              color="dune.dune"
              _hover={{
                bg: isDestructive ? "blaze.600" : "blaze.300",
              }}
              _active={{
                bg: isDestructive ? "blaze.700" : "blaze.300",
              }}
            >
              {confirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
